import { useLocation, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { NotFound } from '../../NotFound';
import { PageHeader, PageShell } from '../../../components/ui/PageHeader';
import { TabBar, type TabItem } from '../../../components/ui/Tabs';
import { OverviewTab } from './OverviewTab';
import { IncomeExpensesTab } from './IncomeExpensesTab';
import { ToolCostsTab } from './ToolCostsTab';
import { CustomersTab } from './CustomersTab';
import { FinanceDataNotes, FinanceIssuesProvider, useFinanceIssues } from './DataIssues';
import { useSubscriptionsSummary } from '../../../hooks/useFinance';
import { useAccess } from '../../../hooks/useAccess';
import { RangeSelector, windowForFullMonths } from './IncomeExpensesTab';
import { monthsSinceFinanceStart } from './formatters';

type FinanceTab = 'overview' | 'customers' | 'pnl' | 'tools';

const TABS: readonly TabItem<FinanceTab>[] = [
  { id: 'overview',  label: 'Overview' },
  { id: 'customers', label: 'Customers' },
  { id: 'pnl',       label: 'P&L & Cash' },
  { id: 'tools',     label: 'Tool costs' },
];

/**
 * Tabs that were folded into another one. Subscriptions' contract-expiry
 * timeline and MRR by plan now sit on the Overview; Revenue & Cash
 * split into the receivables on Customers and invoiced/collected on P&L & Cash.
 */
const MOVED: Record<string, FinanceTab> = {
  subscriptions: 'overview',
  revenue: 'pnl',
};

/** What an old link's tab was called, for the line that says where it went. */
const MOVED_LABEL: Record<string, string> = {
  subscriptions: 'Subscriptions',
  revenue: 'Revenue & Cash',
};

function isFinanceTab(value: string | undefined): value is FinanceTab {
  return TABS.some((t) => t.id === value);
}

/**
 * The MRR caveats (contracts whose Odoo state has not synced, engine warnings)
 * qualify every tab's numbers, not only the Overview's, so the page reports
 * them whichever tab is open. The chip dedupes them against the Overview's own.
 */
function SubscriptionNotes() {
  const { data } = useSubscriptionsSummary();
  useFinanceIssues('page.subscriptions', data?.summary.issues);
  return null;
}

/** "3m" → the 3-full-month window; anything else (or none) → since the start of the books. */
export function monthsFromRange(range: string | null): number {
  const full = range ? /^(\d{1,2})m$/.exec(range) : null;
  const sinceStart = monthsSinceFinanceStart();
  if (!full) return sinceStart;
  return Math.min(windowForFullMonths(Number(full[1])), sinceStart);
}

/** The inverse: null for the default window, so the plain URL stays plain. */
export function rangeFromMonths(months: number): string | null {
  return months >= monthsSinceFinanceStart() ? null : `${months - 1}m`;
}

interface FinancePageProps {
  tab?: string;
}

export function FinancePage({ tab }: FinancePageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const movedFrom = (location.state as { movedFrom?: string } | null)?.movedFrom ?? null;
  const { canAccess } = useAccess();
  // One period for the whole page: P&L & Cash and Tool costs read the same
  // control, which sits in the page header so it visibly drives every number.
  // The period lives in the URL (?range=3m), so a link or a reload keeps it.
  const [searchParams, setSearchParams] = useSearchParams();
  const months = monthsFromRange(searchParams.get('range'));
  const setMonths = (next: number) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        const range = rangeFromMonths(next);
        if (range === null) params.delete('range');
        else params.set('range', range);
        return params;
      },
      { replace: true },
    );
  const periodTab = tab === 'pnl' || tab === 'tools';

  // Customers is its own access feature: somebody who should see who has paid
  // does not thereby get the P&L, and vice versa.
  const canSeeCustomers = canAccess('finance.customers');
  const visibleTabs = canSeeCustomers ? TABS : TABS.filter((t) => t.id !== 'customers');

  if (tab && MOVED[tab]) {
    // An old link lands where its content went, and the page says so rather than switching silently.
    return <Navigate to={`/finance/${MOVED[tab]}`} replace state={{ movedFrom: tab }} />;
  }
  if (tab && !isFinanceTab(tab)) return <NotFound />;
  if (!tab || !isFinanceTab(tab)) {
    return <Navigate to="/finance/overview" replace />;
  }
  if (tab === 'customers' && !canSeeCustomers) {
    return <Navigate to="/finance/overview" replace />;
  }

  return (
    <FinanceIssuesProvider>
      <SubscriptionNotes />
      <PageShell>
        <PageHeader eyebrow="Departments"
          title="Finance"
          actions={periodTab ? <RangeSelector months={months} onChange={setMonths} /> : undefined}
          info="Revenue, cash and costs, synced from Odoo daily at 07:00. Customers come from the closed-won deals in Attio, matched to their Odoo partner."
          meta={
            <>
              <span>Synced from Odoo daily at 07:00</span>
              {/* Holds the chip's height before the notes arrive, so the tabs below never shift. */}
              <span className="inline-flex min-h-[22px] items-center">
                <FinanceDataNotes />
              </span>
            </>
          }
        >
          <TabBar
            tabs={visibleTabs}
            active={tab}
            // The period carries over between the two tabs it drives.
            onSelect={(id) =>
              navigate(
                `/finance/${id}${(id === 'pnl' || id === 'tools') && searchParams.get('range') ? `?range=${searchParams.get('range')}` : ''}`,
              )
            }
          />
        </PageHeader>

        {movedFrom && MOVED[movedFrom] === tab && (
          <p className="-mt-2 text-body-xs text-text-tertiary">
            The {MOVED_LABEL[movedFrom] ?? movedFrom} tab was folded into {TABS.find((t) => t.id === tab)?.label}: its
            figures are on this page.
          </p>
        )}
        {tab === 'overview' && <OverviewTab />}
        {tab === 'customers' && <CustomersTab />}
        {tab === 'pnl' && <IncomeExpensesTab months={months} />}
        {tab === 'tools' && <ToolCostsTab months={months} />}
      </PageShell>
    </FinanceIssuesProvider>
  );
}
